import { useCallback } from 'react';
import type { MapView } from '../constructors';
import { useRendererDiagnosticContext } from '../../monitoring/renderer-diagnostic-context';
import { fetchMainProcessContext } from '../../monitoring/main-process-diagnostic-context';
import { getMapDiagnosticContext } from './map-diagnostic-context';

/**
 * Returns a function that composes the full diagnostic context logged for map
 * events, pulling each piece from its own source: the map context (this module),
 * the renderer context (monitoring), and the electron main-process context
 * (monitoring, over IPC). Callers log the result as `diagnosticContext`.
 *
 * The composition lives in map space on purpose — the shared `monitoring` module
 * has no knowledge of the map. The `main` type is inferred through
 * `fetchMainProcessContext` so this file never imports @m43/bridge-interface
 * directly (the esri folder is not allowed to).
 */
export function useCollectMapDiagnosticContext() {
    const getRendererDiagnosticContext = useRendererDiagnosticContext();

    return useCallback(
        async (
            mapView: MapView | undefined,
            mapRef: React.MutableRefObject<HTMLDivElement | null>,
            isEmbeddedMap: boolean
        ) => {
            const map = getMapDiagnosticContext(mapView, mapRef, isEmbeddedMap);
            const renderer = getRendererDiagnosticContext();
            const main = await fetchMainProcessContext();

            return { map, renderer, main };
        },
        [getRendererDiagnosticContext]
    );
}
